import { IssueReportStatus } from './../pages/issueReports/util/issueReportHelpers';
import { Action, handleActions } from 'redux-actions';
import { allTenantDependentDataCleared } from '../action/authenticationEvents';
import {
    AssetsFetchedPayload,
    CreatedIssueReportPayload,
    IssueReportFetchedPayload,
    IssueReportStatusUpdatedPayload,
    IssueReportUpdatedPayload,
    IssueReportsFetchedPayload,
} from '../action/issueReportsEvents';
import {
    ApartmentAssets,
    DetailedIssueReport,
    DoorStatus,
    IssueReport,
    Thing,
} from '../api/types';
import Events from '../action/event';
import Command from '../action/command';

/** Where the list fetch stands — kept apart from the list itself, because an
 *  empty list means "none" only once a fetch has actually succeeded. */
export type IssueReportsFetchStatus = 'idle' | 'loading' | 'loaded' | 'failed';

export interface IssueReportsState {
    issueReport: DetailedIssueReport | null;
    issueReports: IssueReport[];
    issueReportsFetchStatus: IssueReportsFetchStatus;
    issueReportCreated: boolean;
    /** The last create attempt failed. Kept apart from `issueReportCreated`
     *  so the form can tell "it worked" from "it did not" — collapsing the two
     *  is what let a failed create navigate away as if it had been saved. */
    issueReportCreateFailed: boolean;
    createdIssueReportId: string | null;
    doorStatus: Record<number, DoorStatus>;
    assets: ApartmentAssets[];
    things: Thing[];
}

export const initialState: IssueReportsState = {
    issueReport: null,
    issueReports: [],
    issueReportsFetchStatus: 'idle',
    issueReportCreated: false,
    issueReportCreateFailed: false,
    createdIssueReportId: null,
    doorStatus: {},
    assets: [],
    things: [],
};

/* eslint-disable max-len */
export const reducer = handleActions<IssueReportsState, any>(
    {
        [Command.IssueReport.fetchIssueReports.toString()]: (
            state = initialState,
        ) => ({
            ...state,
            issueReportsFetchStatus: 'loading',
        }),

        [Events.IssueReport.issueReportsFetched.toString()]: (
            state = initialState,
            action: Action<IssueReportsFetchedPayload>,
        ) => {
            if (!state) {
                return state;
            }

            return {
                ...state,
                issueReports: action.payload.issueReports,
                issueReportsFetchStatus: 'loaded',
            };
        },

        [Events.IssueReport.issueReportsFetchFailed.toString()]: (
            state = initialState,
        ) => ({
            ...state,
            issueReportsFetchStatus: 'failed',
        }),

        [Events.IssueReport.assetsFetched.toString()]: (
            state = initialState,
            action: Action<AssetsFetchedPayload>,
        ) => {
            if (!state) {
                return state;
            }

            return {
                ...state,
                assets: action.payload.assets,
            };
        },

        [Events.IssueReport.issueReportFetched.toString()]: (
            state = initialState,
            action: Action<IssueReportFetchedPayload>,
        ) => {
            if (!state) {
                return state;
            }

            return {
                ...state,
                issueReport: action.payload.issueReport,
            };
        },

        [Events.IssueReport.issueReportUpdated.toString()]: (
            state = initialState,
            action: Action<IssueReportUpdatedPayload>,
        ) => {
            if (!state) {
                return state;
            }

            return {
                ...state,
                issueReports: state.issueReports.map((issueReport) => {
                    if (issueReport.id !== action.payload.id) {
                        return issueReport;
                    }

                    return {
                        ...issueReport,
                        enabled: action.payload.enabled,
                    };
                }),
            };
        },
        [Events.IssueReport.createdIssueReport.toString()]: (
            state = initialState,
            action: Action<CreatedIssueReportPayload>,
        ) => {
            if (!state) {
                return state;
            }

            return {
                ...state,
                issueReportCreated: action.payload.success,
                // Absent on the form's reset dispatch, which is what clears it.
                issueReportCreateFailed: action.payload.failed ?? false,
                // Cleared alongside the flag when the form resets it, so a
                // later create can't be linked to a stale id.
                createdIssueReportId: action.payload.success
                    ? action.payload.issueReportId ?? null
                    : null,
            };
        },
        [allTenantDependentDataCleared.toString()]: (
            state = initialState,
            action: Action<{}>,
        ) => {
            return {
                ...initialState,
            };
        },

        [Events.IssueReport.updatedIssueReportStatus.toString()]: (
            state = initialState,
            action: Action<IssueReportStatusUpdatedPayload>,
        ) => {
            if (!state) {
                return state;
            }

            const updatedIssueReports = state.issueReports.map(
                (issueReport) => {
                    if (issueReport.id === action.payload.id) {
                        return {
                            ...issueReport,
                            state: action.payload.status as IssueReportStatus,
                        };
                    }
                    return issueReport;
                },
            );

            return {
                ...state,
                issueReports: updatedIssueReports,
            };
        },
    },
    initialState,
);
